import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import LoadingSpinner from "../LoadingSpinner";
import ErrorMessage from "../ErrorMessage";

const SCROLL_BOTTOM_THESHOLD_PX = 20;

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: 480,
    overflowY: "scroll",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  iframe: {
    width: "100%",
    height: 2000,
    border: "none",
    display: "block",
    overflow: "hidden",
    pointerEvents: "none",
  },
}));

const TermsAndConditions = ({ locale, onReachedBottom, hasReachedBottom }) => {
  const classes = useStyles();
  const [srcDoc, setSrcDoc] = React.useState("");
  const [isLoading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const iframeRef = React.useRef(null);

  const handleIframeLoad = () => {
    try {
      const height = iframeRef.current?.contentWindow?.document?.body?.scrollHeight;
      if (height) iframeRef.current.style.height = height + "px";
    } catch {
      iframeRef.current.style.height = "3000px";
    }
  };

  const cleanTermsHtml = (rawHtml) => {
    const doc = new DOMParser().parseFromString(rawHtml, "text/html");
    doc.querySelectorAll('[data-elementor-type="header"], [data-elementor-type="footer"], ' + "header.elementor-location-header, footer.elementor-location-footer").forEach((el) => el.remove());
    doc.querySelectorAll("script, noscript").forEach((el) => el.remove());
    const head = doc.head || doc.getElementsByTagName("head")[0];
    if (head && !head.querySelector("base")) {
      const base = doc.createElement("base");
      base.setAttribute("href", "https://lux.capital/");
      base.setAttribute("target", "_blank");
      head.prepend(base);
    }
    return "<!doctype html>\n" + doc.documentElement.outerHTML;
  };

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const PROXY_URL = "/lux/terms";
        const res = await fetch(`${PROXY_URL}?lang=${locale}`);
        if (!res.ok) throw new Error("app.termsAndConditions.error");
        const html = await res.text();
        if (cancelled) return;
        setSrcDoc(cleanTermsHtml(html));
      } catch (err) {
        if (cancelled) return;
        setError(err.message || String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (isLoading) return <LoadingSpinner message={<FormattedMessage id="app.loading.fetching.terms" />} />;

  if (error) {
    return (
      <ErrorMessage>
        <FormattedMessage id={error} />
      </ErrorMessage>
    );
  }

  return (
    <>
      <Box
        className={classes.container}
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          const reachedBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THESHOLD_PX;
          if (reachedBottom && onReachedBottom) {
            onReachedBottom();
          }
        }}
      >
        <iframe ref={iframeRef} onLoad={handleIframeLoad} className={classes.iframe} title="Lux Capital Terms" srcDoc={srcDoc} sandbox="allow-same-origin allow-popups allow-scripts" scrolling="no" />
      </Box>
      {!hasReachedBottom && (
        <Typography variant="caption" color="primary">
          <FormattedMessage id="app.termsAndConditions.rollToBottom" />
        </Typography>
      )}
    </>
  );
};

export default TermsAndConditions;
